/**
 * Erasing, from the hand going down to the board being changed (SRS C4).
 *
 * Three modes, all driven by the same circle:
 *
 *  - **stroke** — sweep over ink and each whole line it touches goes
 *  - **area**   — sweep over ink and only the part under the circle goes; the rest
 *                 of the line stays exactly where it was
 *  - **object** — one click removes the one thing under it, whole
 *
 * The board changes on screen the instant the circle passes over it. The shared copy
 * is written once, when the hand lifts, which is what makes a whole wipe a single
 * press of undo rather than one press per line.
 */

import type { EraserMode, Point, Rect, Stroke, Unsubscribe } from '@opal/shared';

import { isEmptyEdit, type BoardEdit } from './board-edit.js';
import { eraseFromStroke, eraserArea, stepsBetween, strokeTouched } from './erase.js';

/** The part of the board an eraser touches. The ink session provides all of it. */
export interface ErasableInk {
  /** The strokes whose boxes touch this area, oldest first. */
  near(area: Rect): readonly Stroke[];
  remove(strokeId: string): boolean;
  accept(stroke: Stroke): void;
  /**
   * True when this piece of the board can only come off whole.
   *
   * Ink and shapes are lines, and part of a line is still a line. A text box or a
   * sticky note is not: half of one is nothing at all, so the eraser takes those
   * whole however it is set.
   */
  wholeOnly(strokeId: string): boolean;
}

export interface EraseSessionOptions {
  readonly ink: ErasableInk;
  /** Read fresh when the hand goes down, and held for the whole sweep. */
  readonly mode: () => EraserMode;
  /** The circle's radius in canvas units, which changes with zoom. Read fresh. */
  readonly radius: () => number;
}

export interface EraseSession {
  /** The hand went down. */
  begin(at: Point): void;
  /** The hand moved. Every position between is erased at, not only the last one. */
  extend(points: readonly Point[]): void;
  /** The hand lifted: what changed is handed over to be shared and saved. */
  finish(): void;
  /** The gesture was taken away. What is already erased stays erased. */
  cancel(): void;
  /** Called with each finished erase, for sharing and saving. */
  onEdited(handler: (edit: BoardEdit) => void): Unsubscribe;
  readonly erasing: boolean;
}

interface Batch {
  /** The mode this sweep started in, so changing it mid-sweep cannot rewrite it. */
  readonly mode: EraserMode;
  readonly removed: Set<string>;
  /** Pieces that survived, by id, so one later rubbed out again can be dropped. */
  readonly added: Map<string, Stroke>;
  /** Where the circle was last, for filling in a fast movement. */
  last: Point;
}

interface State {
  readonly options: EraseSessionOptions;
  readonly handlers: Set<(edit: BoardEdit) => void>;
  batch: Batch | null;
}

/** Takes a whole stroke off the board and remembers that it went. */
function removeWhole(state: State, batch: Batch, stroke: Stroke): void {
  if (!state.options.ink.remove(stroke.id)) return;
  batch.removed.add(stroke.id);
}

/** SRS C4, area mode — the original goes, and what survived goes back on. */
function rubOut(state: State, batch: Batch, stroke: Stroke, centre: Point, radius: number): void {
  // Nothing that cannot be cut is ever cut: it goes whole, or it stays.
  if (state.options.ink.wholeOnly(stroke.id)) {
    if (strokeTouched(stroke, centre, radius)) removeWhole(state, batch, stroke);
    return;
  }
  const pieces = eraseFromStroke(stroke, centre, radius);
  if (!pieces) return;
  removeWhole(state, batch, stroke);
  for (const piece of pieces) {
    state.options.ink.accept(piece);
    batch.added.set(piece.id, piece);
  }
}

/** One position of the circle, applied to everything under it. */
function eraseAt(state: State, batch: Batch, centre: Point): void {
  const radius = state.options.radius();
  const strokes = state.options.ink.near(eraserArea(centre, radius));

  if (batch.mode === 'area') {
    for (const stroke of strokes) rubOut(state, batch, stroke, centre, radius);
    return;
  }
  for (const stroke of strokes) {
    if (strokeTouched(stroke, centre, radius)) removeWhole(state, batch, stroke);
  }
}

/**
 * SRS C4, object mode — a click removes the one thing under it.
 *
 * The last stroke in the list is the one drawn most recently, so a click on crossing
 * lines takes the one lying on top, which is the one the teacher can see.
 */
function eraseOne(state: State, batch: Batch, centre: Point): void {
  const radius = state.options.radius();
  const strokes = state.options.ink.near(eraserArea(centre, radius));
  for (let index = strokes.length - 1; index >= 0; index -= 1) {
    const stroke = strokes[index];
    if (!stroke || !strokeTouched(stroke, centre, radius)) continue;
    removeWhole(state, batch, stroke);
    return;
  }
}

/**
 * What the sweep did, seen from outside.
 *
 * A piece that was rubbed out again later in the same sweep never reached anybody
 * else, and neither did a stroke that only ever existed as such a piece, so neither
 * is worth sending.
 */
function changeOf(batch: Batch): BoardEdit {
  return {
    removed: [...batch.removed].filter((id) => !batch.added.has(id)),
    added: [...batch.added.values()].filter((stroke) => !batch.removed.has(stroke.id)),
    // The eraser never changes a stroke in place: what survives is a new stroke.
    replaced: [],
    // Nor does it ever put anything on the board; it only ever takes things off.
    objectsAdded: [],
    objectsChanged: [],
  };
}

function finish(state: State): void {
  const batch = state.batch;
  state.batch = null;
  if (!batch) return;
  const change = changeOf(batch);
  if (isEmptyEdit(change)) return;
  for (const handler of state.handlers) handler(change);
}

export function createEraseSession(options: EraseSessionOptions): EraseSession {
  const state: State = {
    options,
    handlers: new Set<(edit: BoardEdit) => void>(),
    batch: null,
  };

  return {
    get erasing() {
      return state.batch !== null;
    },

    begin(at) {
      const batch: Batch = {
        mode: options.mode(),
        removed: new Set<string>(),
        added: new Map<string, Stroke>(),
        last: at,
      };
      state.batch = batch;
      if (batch.mode === 'object') eraseOne(state, batch, at);
      else eraseAt(state, batch, at);
    },

    extend(points) {
      const batch = state.batch;
      // One click, one object: dragging in object mode must not sweep the board.
      if (!batch || batch.mode === 'object') return;
      for (const point of points) {
        for (const step of stepsBetween(batch.last, point, options.radius())) {
          eraseAt(state, batch, step);
        }
        batch.last = point;
      }
    },

    finish() {
      finish(state);
    },

    cancel() {
      // What has gone is gone from this screen already; sharing it is what stops the
      // two screens disagreeing about a board they are both looking at.
      finish(state);
    },

    onEdited(handler) {
      state.handlers.add(handler);
      return () => {
        state.handlers.delete(handler);
      };
    },
  };
}
